import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, X, Check, Sparkles, Loader2 } from "lucide-react";
import { clsx } from "clsx";
import {
  isSpeechRecognitionSupported,
  parseVoiceText,
  startVoiceCapture,
  type VoiceCaptureHandle,
} from "../lib/voice";
import { isAiAvailable, structureTask } from "../lib/ai-pwa";
import { useTaskStore } from "../stores/taskStore";
import { useUiStore } from "../stores/uiStore";
import { haptic } from "../lib/haptics";
import { Textarea } from "./ui/Textarea";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function VoiceCaptureSheet({ open, onClose }: Props) {
  const createTask = useTaskStore((s) => s.createTask);
  const showToast = useUiStore((s) => s.showToast);
  const [transcript, setTranscript] = useState("");
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const handleRef = useRef<VoiceCaptureHandle | null>(null);

  const supported = isSpeechRecognitionSupported();
  const aiAvailable = isAiAvailable();

  // 開いたら自動で録音開始
  useEffect(() => {
    if (!open) return;
    setTranscript("");
    setError(null);
    if (!supported) {
      setError("このブラウザは音声認識をサポートしていません");
      return;
    }
    haptic("light");
    const handle = startVoiceCapture({
      onStart: () => setRecording(true),
      onPartial: (text) => setTranscript(text),
      onFinal: (text) => setTranscript(text),
      onError: (err) => {
        setError(err);
        setRecording(false);
      },
      onEnd: () => setRecording(false),
    });
    handleRef.current = handle;
    return () => {
      handleRef.current?.abort();
      handleRef.current = null;
    };
  }, [open, supported]);

  const stopRecording = () => {
    handleRef.current?.stop();
  };

  const cancel = () => {
    handleRef.current?.abort();
    handleRef.current = null;
    setTranscript("");
    setRecording(false);
    onClose();
  };

  const confirm = async () => {
    if (!transcript.trim()) {
      cancel();
      return;
    }
    handleRef.current?.stop();
    const parsed = parseVoiceText(transcript);
    haptic("success");
    await createTask({
      title: parsed.title,
      dueDate: parsed.dueDate,
      priority: parsed.priority ?? "medium",
    });
    showToast(`「${parsed.title}」を追加しました`);
    setTranscript("");
    onClose();
  };

  const confirmWithAi = async () => {
    if (!transcript.trim()) {
      cancel();
      return;
    }
    handleRef.current?.stop();
    setAiLoading(true);
    try {
      const ai = await structureTask(transcript, {
        now: new Date().toISOString(),
      });
      haptic("success");
      await createTask({
        title: ai.title,
        description: ai.description || undefined,
        dueDate: ai.dueDate,
        priority: ai.priority,
      });
      showToast(`AI整理: 「${ai.title}」を追加しました`);
      setTranscript("");
      onClose();
    } catch (err) {
      // フォールバック: ローカルパースで追加
      console.warn("[Flow] AI structure failed, falling back:", err);
      const parsed = parseVoiceText(transcript);
      await createTask({
        title: parsed.title,
        dueDate: parsed.dueDate,
        priority: parsed.priority ?? "medium",
      });
      showToast(`AI失敗→ローカル解析: 「${parsed.title}」`);
      setTranscript("");
      onClose();
    } finally {
      setAiLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg/95 backdrop-blur-md dark:bg-bg-dark/95">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pb-3" style={{ paddingTop: "max(env(safe-area-inset-top), 16px)" }}>
        <button
          type="button"
          aria-label="キャンセル"
          onClick={cancel}
          className="flex h-11 w-11 items-center justify-center rounded-full text-muted dark:text-muted-dark"
        >
          <X className="h-5 w-5" />
        </button>
        <span className="text-sm font-semibold">音声でタスクを追加</span>
        <button
          type="button"
          aria-label="保存"
          onClick={() => void confirm()}
          disabled={!transcript.trim()}
          className={clsx(
            "flex h-11 w-11 items-center justify-center rounded-full",
            transcript.trim()
              ? "text-brand-600 dark:text-brand-300"
              : "text-dim cursor-not-allowed"
          )}
        >
          <Check className="h-5 w-5" />
        </button>
      </div>

      {/* Transcript / preview area */}
      <div className="flex flex-1 flex-col items-center justify-center px-6">
        <div className="w-full max-w-md">
          {error && (
            <div className="mb-4 text-center text-sm text-danger-500">
            <p>{error}</p>
            <p className="mt-2 text-muted dark:text-muted-dark">
              マイク権限を許可してから再度試してください。
            </p>
            </div>
          )}
          <Textarea
            aria-label="タスク入力"
            value={transcript}
            onChange={(event) => setTranscript(event.target.value)}
            placeholder={recording ? "話してください..." : "例: 明日14時 病院に電話"}
            className="min-h-36 border-white/10 bg-card/80 text-center text-lg font-medium leading-relaxed shadow-sm dark:bg-card-dark/80"
          />
          {transcript.trim() && (() => {
            const parsed = parseVoiceText(transcript);
            return (
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs">
                {parsed.dueDate && (
                  <span className="rounded-full bg-brand-500/10 px-3 py-1 font-medium text-brand-700 dark:text-brand-200">
                    📅 {parsed.dueDate}
                  </span>
                )}
                {parsed.priority === "high" && (
                  <span className="rounded-full bg-danger-500/10 px-3 py-1 font-medium text-danger-600">
                    🔥 高優先
                  </span>
                )}
                {parsed.priority === "low" && (
                  <span className="rounded-full bg-success-500/10 px-3 py-1 font-medium text-success-600">
                    🌱 低優先
                  </span>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Mic / Actions */}
      <div className="flex flex-col items-center pb-10" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 40px)" }}>
        <button
          type="button"
          onClick={recording ? stopRecording : () => void confirm()}
          disabled={aiLoading}
          className={clsx(
            "flex h-20 w-20 items-center justify-center rounded-full shadow-elevated transition-transform active:scale-95",
            recording
              ? "bg-danger-500 text-white animate-pulse"
              : "bg-brand-500 text-white",
            aiLoading && "opacity-50"
          )}
          aria-label={recording ? "録音停止" : "確定"}
        >
          {recording ? <MicOff className="h-8 w-8" /> : <Mic className="h-8 w-8" />}
        </button>
        <p className="mt-3 text-xs text-muted dark:text-muted-dark">
          {recording ? "タップで停止" : aiLoading ? "AI 整理中…" : "タップで追加"}
        </p>

        {/* AI structure button (only when transcript exists and AI available) */}
        {!recording && transcript && aiAvailable && (
          <button
            type="button"
            onClick={() => void confirmWithAi()}
            disabled={aiLoading}
            className={clsx(
              "mt-3 flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition",
              "bg-gradient-to-r from-brand-500 to-info-500 text-white shadow-md active:scale-95",
              aiLoading && "opacity-50 cursor-not-allowed"
            )}
          >
            {aiLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            AI で整理して追加
          </button>
        )}

        <p className="mt-3 text-[10px] text-dim">
          ヒント: 「明日 14時 病院に電話」「来週月曜 ミーティング」「至急 報告書」
        </p>
      </div>
    </div>
  );
}
